import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Contratos e termos: a tela interna da Westhetics (docs/api/documentos.md, "Templates";
 * docs/areas/documentos-e-assinaturas.md, RN-DOC-11 a 15 e 42 a 47). O documento é Markdown no
 * dialeto de legal design; cada publicação é uma versão imutável.
 */

export type DocumentType = 'consent_form' | 'contract' | 'anamnesis' | 'standalone' | 'other'
export type Category =
  | 'service_contract'
  | 'model_patient_contract'
  | 'informed_consent'
  | 'anamnesis'
  | 'standalone_form'
  | 'refund'
  | 'general_contract'
  | 'other'
export type Audience = 'patient' | 'student' | 'employee' | 'third_party'
export type TemplateState = 'draft' | 'published' | 'archived'

export interface PersonRef {
  id: string | null
  name: string
}

export interface LintDiagnostic {
  severity: 'error' | 'warning'
  code: string
  message: string
  line: number | null
}

export interface VersionSummary {
  id: string
  number: number
  label: string
  hash: string
  shortHash: string
  publishedAt: string
  publishedBy: PersonRef
  updateNote: string | null
  reviewedBy: string
  reviewedAt: string
  requiresIcpCertificate: boolean
  variables: string[]
}

export interface Version extends VersionSummary {
  markdown: string
}

export interface TemplateDraft {
  markdown: string
  revision: number
  updatedAt: string
  updatedBy: PersonRef
  diagnostics: LintDiagnostic[]
}

export interface Template {
  id: string
  name: string
  type: DocumentType
  category: Category
  audience: Audience
  procedure: string | null
  selectableIn: ('procedure' | 'standalone')[]
  isIncluded: boolean
  isDefault: boolean
  state: TemplateState
  currentVersion: VersionSummary | null
  archivedAt: string | null
  createdAt: string
  draft: TemplateDraft | null
  /** A trava do rascunho, também sem rascunho: é o que o editor manda na primeira gravação. */
  draftRevision: number
  activations: { total: number }
  versionCount: number
  hasUnpublishedChanges: boolean
  procedures: { id: string; name: string; isRequired: boolean }[]
  usage: { clinicCount: number }
  /**
   * Falta a revisão jurídica (RN-DOC-53): a versão vigente foi publicada pela carga do sistema anterior sem
   * revisão, ou ainda não há versão e o rascunho veio do sistema anterior.
   */
  isLegalReviewPending: boolean
  /** A origem no sistema anterior; nulo no documento escrito na Westhetics. */
  legacy: LegacyOrigin | null
}

/** O modelo do sistema anterior de que o documento veio, como a carga gravou no front matter. */
export interface LegacyOrigin {
  legacyId: string
  legacyName: string | null
  /** Clínicas com o modelo ativo no sistema anterior. */
  activeClinics: number | null
  /** Envios nos 90 dias antes da exportação. */
  sendsLast90Days: number | null
}

/** `review`: a fila da revisão jurídica do que veio do sistema anterior (RN-DOC-53). */
export type TemplateFilter = 'unpublished' | 'review' | 'published' | 'archived' | 'all'

export interface TemplateListMeta extends ListMeta {
  counts: Record<TemplateFilter, number>
}

export interface HistoryEntry {
  id: string
  occurredAt: string
  action:
    | 'created'
    | 'updated'
    | 'draft_updated'
    | 'draft_discarded'
    | 'published'
    | 'linked'
    | 'unlinked'
    | 'archived'
    | 'restored'
  actor: PersonRef
  summary: string | null
  version: { number: number; label: string } | null
}

/** Um documento citado por outro: o que saiu, o que entrou, o que a clínica usa hoje. */
export interface TemplateRef {
  templateId: string
  name: string
}

/** O documento numa clínica (`GET /documents/templates/:id/clinics`; RN-DOC-48 a 50). */
export interface TemplateClinic {
  clinicId: string
  /** O nome fantasia, ou a razão social quando não há. */
  clinicName: string
  legalName: string
  city: string
  state: string
  clinicStatus: 'active' | 'blocked'
  /** A clínica emite para o público do documento; sem isso, não dá para ativar. */
  hasAudience: boolean
  /** Ativação vigente. O documento também pode chegar por `procedures`. */
  isActive: boolean
  /** A última ativação gravada, mesmo desativada; tudo nulo quando nunca houve. */
  source: 'default' | 'root' | null
  activatedAt: string | null
  activatedBy: PersonRef | null
  deactivatedAt: string | null
  deactivatedBy: PersonRef | null
  /** Procedimentos da clínica que carregam o documento: ele chega sem ativação (RN-DOC-43). */
  procedures: { id: string; name: string }[]
  /** Em categoria exclusiva, o outro documento que a clínica usa hoje (RN-DOC-06). */
  activeInCategory: TemplateRef | null
}

/** `active`: o documento chega à clínica, por ativação ou por procedimento; `inactive`: não chega. */
export type TemplateClinicFilter = 'active' | 'inactive' | 'all'

export interface TemplateClinicListMeta extends ListMeta {
  counts: Record<TemplateClinicFilter, number>
  /** O padrão do catálogo que assume quando o documento sai de uma clínica; nulo quando não há. */
  fallback: TemplateRef | null
}

export interface SetTemplateClinicsInput {
  /** O documento a ativar ou desativar: o da tela ou, ao desfazer uma troca, o que tinha saído. */
  templateId: string
  activateIn?: string[]
  deactivateIn?: string[]
}

/** O que mudou de fato: ativar o já ativo e desativar o que não está ativo ficam de fora. */
export interface SetTemplateClinicsResult {
  templateId: string
  activations: { total: number }
  activated: {
    clinicId: string
    clinicName: string
    source: 'default' | 'root'
    activatedAt: string
    /** O documento que saiu para este entrar (categoria exclusiva). */
    replaced: TemplateRef | null
  }[]
  deactivated: {
    clinicId: string
    clinicName: string
    deactivatedAt: string
    /** O padrão do catálogo que assumiu, onde a clínica não pode ficar sem. */
    replacedBy: TemplateRef | null
  }[]
}

export interface CreateTemplateInput {
  name: string
  type: DocumentType
  category: Category
  audience: Audience
  /** Só no termo de consentimento; nas outras categorias o campo não vai. */
  procedure?: string
  draft: { markdown: string }
}

export interface UpdateTemplateInput {
  name?: string
  category?: Category
  /** Só antes da primeira versão publicada (RN-DOC-42). */
  audience?: Audience
  procedure?: string
  selectableIn?: ('procedure' | 'standalone')[]
  isDefault?: boolean
  draft?: { markdown: string; revision: number }
}

export interface PublishInput {
  label: string
  reviewedBy: string
  reviewedAt: string
  /** Obrigatória a partir da versão 2; na primeira, o campo não vai. */
  updateNote?: string
  requiresIcpCertificate: boolean
}

export const templateKeys = {
  all: ['templates'] as const,
  list: (params: object) => ['templates', 'list', params] as const,
  detail: (id: string) => ['templates', 'detail', id] as const,
  versions: (id: string) => ['templates', 'versions', id] as const,
  version: (id: string, number: number) => ['templates', 'version', id, number] as const,
  history: (id: string, filter: string) => ['templates', 'history', id, filter] as const,
  clinics: (id: string, params: object) => ['templates', 'clinics', id, params] as const,
}

export function useTemplates(params: {
  filter: TemplateFilter
  search?: string
  audience?: Audience | ''
  category?: Category | ''
  page?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.list(params),
    queryFn: () =>
      api.list<Template, TemplateListMeta>('/documents/templates', {
        filter: params.filter,
        search: params.search,
        audience: params.audience || undefined,
        category: params.category || undefined,
        page: params.page ?? 1,
        perPage: 25,
      }),
    placeholderData: (previous) => previous,
  })
}

/**
 * O próximo da fila da revisão jurídica (RN-DOC-53): o primeiro do segmento `review`, na ordem da fila, que não é o
 * documento aberto. Nulo quando a fila só tem ele, ou está vazia.
 */
export function useNextInReview(currentId: string, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.list({ filter: 'review', after: currentId }),
    queryFn: async () => {
      const page = await api.list<Template, TemplateListMeta>('/documents/templates', {
        filter: 'review',
        page: 1,
        perPage: 10,
      })
      return page.data.find((item) => item.id !== currentId) ?? null
    },
    enabled,
  })
}

export function useTemplate(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.detail(id ?? ''),
    queryFn: () => api.get<Template>(`/documents/templates/${id}`),
    enabled: Boolean(id),
  })
}

export function useTemplateVersions(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.versions(id ?? ''),
    queryFn: async () =>
      (await api.list<VersionSummary>(`/documents/templates/${id}/versions`)).data,
    enabled: Boolean(id),
  })
}

/** Uma versão com o Markdown. Imutável: uma vez lida, vale para sempre. */
export function useTemplateVersion(id: string | undefined, number: number | null | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.version(id ?? '', number ?? 0),
    queryFn: () => api.get<Version>(`/documents/templates/${id}/versions/${number}`),
    enabled: Boolean(id) && typeof number === 'number' && number > 0,
    staleTime: Infinity,
  })
}

export function useTemplateHistory(
  id: string | undefined,
  filter: 'all' | 'versions',
  page: number,
) {
  const api = useApi()
  return useQuery({
    queryKey: [...templateKeys.history(id ?? '', filter), page],
    queryFn: () =>
      api.list<HistoryEntry>(`/documents/templates/${id}/history`, { filter, page, perPage: 25 }),
    enabled: Boolean(id),
    placeholderData: (previous) => previous,
  })
}

export function useCreateTemplate() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateTemplateInput) =>
      api.post<Template>('/documents/templates', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: templateKeys.all })
    },
  })
}

/** Edita campos e grava o rascunho. O detalhe em cache recebe a resposta, sem novo `GET`. */
export function useUpdateTemplate(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateTemplateInput) =>
      api.put<Template>(`/documents/templates/${id}`, input),
    onSuccess: (template) => {
      queryClient.setQueryData(templateKeys.detail(id), template)
      void queryClient.invalidateQueries({ queryKey: ['templates', 'list'] })
    },
  })
}

function useTemplateMutation<TInput>(
  id: string,
  mutationFn: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
  onDone?: () => void,
) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TInput) => mutationFn(api, input),
    onSuccess: () => {
      onDone?.()
      return queryClient.invalidateQueries({ queryKey: templateKeys.all })
    },
    meta: { id },
  })
}

/** Apaga o rascunho. O detalhe em cache já recebe o documento sem rascunho: o editor reabre por ele. */
export function useDiscardDraft(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Template>(`/documents/templates/${id}/draft/discard`),
    onSuccess: (template) => {
      queryClient.setQueryData(templateKeys.detail(id), template)
      void queryClient.invalidateQueries({ queryKey: ['templates', 'list'] })
    },
  })
}

export function usePublishVersion(id: string) {
  const idempotency = useIdempotencyKey()
  return useTemplateMutation<PublishInput>(
    id,
    (api, input) =>
      api.post<Version>(`/documents/templates/${id}/versions`, input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    idempotency.reset,
  )
}

export function useArchiveTemplate(id: string) {
  return useTemplateMutation<'archive' | 'restore'>(id, (api, action) =>
    api.post<Template>(`/documents/templates/${id}/${action}`),
  )
}

export function useSetTemplateProcedures(id: string) {
  return useTemplateMutation<{ catalogProcedureId: string; isRequired: boolean }[]>(
    id,
    (api, procedures) => api.put<Template>(`/documents/templates/${id}/procedures`, { procedures }),
  )
}

/** As clínicas com a situação do documento em cada uma. 50 por página: é o que a seleção em lote alcança. */
export function useTemplateClinics(
  id: string | undefined,
  params: { filter: TemplateClinicFilter; search?: string; page?: number },
) {
  const api = useApi()
  return useQuery({
    queryKey: templateKeys.clinics(id ?? '', params),
    queryFn: () =>
      api.list<TemplateClinic, TemplateClinicListMeta>(`/documents/templates/${id}/clinics`, {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: 50,
      }),
    enabled: Boolean(id),
    placeholderData: (previous) => previous,
  })
}

/**
 * Ativa e desativa por clínica. Tudo de contratos e termos é refeito depois: a lista de clínicas, o
 * "em 41 clínicas" do cabeçalho e, numa troca em categoria exclusiva, o documento que saiu.
 */
export function useSetTemplateClinics() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ templateId, activateIn, deactivateIn }: SetTemplateClinicsInput) =>
      api.put<SetTemplateClinicsResult>(`/documents/templates/${templateId}/clinics`, {
        activateIn,
        deactivateIn,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: templateKeys.all }),
  })
}
